import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import {
  ProjectFee,
  ProjectFeeSection,
  ProjectFeeComponent,
  FeeComponentType,
  FeePhase,
  FEE_PHASES,
  Discipline,
  DISCIPLINES,
  disciplineLabel,
  normDiscipline,
  formatCurrency,
} from '../../../../InvoiceRegister/config';
import SectionEditor from './SectionEditor';
import { notifyReadOnly, notifySaveFailure } from '../../../../../utils/supabaseErrors';
import './styles.scss';
import Skeleton from '../../../../../components/Skeleton';
import SaveDot from '../../../../../components/SaveDot';

interface FeesTabProps {
  projectId: number;
  stageId: number | null;
  // Registered by this tab so the modal can flush a pending debounced save
  // before it closes (and before the Budget page refetches on close).
  flushRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  // Where the unmount cleanup parks its fire-and-forget flush promise, so a
  // save started by a tab switch can still be awaited at modal close.
  pendingSaveRef?: React.MutableRefObject<Promise<void> | null>;
}

const FeesTab: React.FC<FeesTabProps> = ({ projectId, stageId, flushRef, pendingSaveRef }) => {
  const supabase = useSupabaseClient();

  const [fee, setFee] = useState<ProjectFee | null>(null);
  const [sections, setSections] = useState<ProjectFeeSection[]>([]);
  // Two-way bound to the project's `has_civil_scope` flag. When on, the fee
  // editor shows separate Structural and Civil add-phase blocks.
  const [civilEnabled, setCivilEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [creating, setCreating] = useState(false);

  // Set while a civil-scope toggle write is in flight so an in-flight loadFee
  // (e.g. on mount) can't clobber the user's just-made toggle with the stale
  // has_civil_scope it read before the write committed.
  const civilWriteInFlightRef = useRef(false);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Snapshot of last-persisted section state, used in amend mode to detect
  // which unlocked existing sections actually changed (so we only send dirty
  // ones to update_project_fee_section, not every section every keystroke).
  const lastSavedJsonRef = useRef<string>('');
  // Guard so a save in flight doesn't overlap with the next debounce tick
  // (we'd otherwise risk double-appending the same new section).
  const savingRef = useRef<boolean>(false);
  // Latest flush() — unmount cleanups are stale closures, so they go through
  // this ref to always fire the current version.
  const flushLatestRef = useRef<() => Promise<void>>(() => Promise.resolve());
  // Last error swallowed by persistFee, so flush() can surface a persistent
  // toast when a close-time save fails (the inline badge unmounts instantly).
  const lastSaveErrorRef = useRef<unknown>(null);
  // Ids the amend-mode append assigned, keyed by _localKey. removeSection
  // consults this after waiting out a save, so a section removed while its
  // append was in flight can still be deleted instead of becoming a phantom
  // DB row.
  const appendedIdByLocalKeyRef = useRef<Map<string, number>>(new Map());

  // Bounded wait for the in-flight save to settle. Returns false if it is
  // still running after maxMs (e.g. a fetch stalled by a dead connection —
  // the supabase client has no request timeout), so callers can bail with a
  // warning instead of hanging the UI forever.
  const waitForSaveIdle = useCallback(async (maxMs = 10000): Promise<boolean> => {
    const start = Date.now();
    while (savingRef.current && Date.now() - start < maxMs) {
      await new Promise((r) => setTimeout(r, 100));
    }
    return !savingRef.current;
  }, []);

  // Park a write in the shared pending-writes chain so the modal's close
  // handler (and the next FeesTab instance's load) wait for it. Chained, not
  // overwritten — an earlier still-in-flight write must stay awaited.
  const parkPendingWrite = useCallback(
    (p: Promise<unknown>) => {
      if (!pendingSaveRef) return;
      const prev = pendingSaveRef.current;
      pendingSaveRef.current = Promise.allSettled(prev ? [prev, p] : [p]).then(() => undefined);
    },
    [pendingSaveRef],
  );

  // A section's components are the inputs that matter for change detection /
  // RPC payloads. We don't compare id / fee_total / progress_claim_count —
  // those are server-owned and not part of "did the user edit this?".
  const serializeSectionForCompare = useCallback(
    (s: ProjectFeeSection) => ({
      id: s.id ?? null,
      phase: s.phase,
      discipline: s.discipline ?? null,
      name: s.name,
      notes: s.notes ?? null,
      is_item_based: s.is_item_based ?? false,
      is_probable: s.is_probable ?? false,
      components: s.components.map((c) => ({
        component_type: c.component_type,
        description: c.description,
        team_member_type: c.team_member_type,
        hours: c.hours,
        rate: c.rate,
        amount: c.amount,
        notes: c.notes ?? null,
      })),
    }),
    []
  );

  const buildSectionPayload = useCallback(
    (s: ProjectFeeSection) => ({
      phase: s.phase,
      discipline: s.discipline ?? null,
      name: s.name,
      notes: s.notes ?? null,
      is_item_based: s.is_item_based ?? false,
      is_probable: s.is_probable ?? false,
      components: s.components.map((c, ci) => ({
        sort_order: ci,
        component_type: c.component_type,
        description: c.description,
        team_member_type: c.team_member_type,
        hours: c.hours,
        rate: c.rate,
        amount: c.amount,
        notes: c.notes ?? null,
      })),
    }),
    []
  );

  const loadFee = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Civil scope is a project-level flag; the fee editor mirrors it.
      const { data: projectRow } = await supabase
        .from('projects')
        .select('has_civil_scope')
        .eq('id', projectId)
        .single();
      // Don't overwrite a toggle the user just made while this load was in
      // flight — their write is the newer truth.
      if (!civilWriteInFlightRef.current) {
        setCivilEnabled(!!projectRow?.has_civil_scope);
      }

      const { data, error: rpcError } = await supabase.rpc('get_project_fee_for_entity', {
        p_project_id: projectId,
        p_stage_id: stageId,
      });
      if (rpcError) throw rpcError;
      if (!data) {
        setFee(null);
        setSections([]);
        lastSavedJsonRef.current = '';
      } else {
        const f = data as ProjectFee;
        setFee(f);
        const normalised = (f.sections || []).map((s, idx) => ({
          ...s,
          sort_order: idx,
          components: (s.components || []).map((c, ci) => ({ ...c, sort_order: ci })),
        }));
        setSections(normalised);
        lastSavedJsonRef.current = JSON.stringify(
          normalised.map(serializeSectionForCompare)
        );
      }
    } catch (err) {
      console.error('Failed to load fee:', err);
      setError(err instanceof Error ? err.message : 'Failed to load fee');
    } finally {
      setLoading(false);
    }
  }, [projectId, stageId, supabase, serializeSectionForCompare]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // A previous instance (tab switch) may have parked a still-in-flight
      // save or delete. Wait it out first, or this load could read pre-save
      // data and seed a stale snapshot that a later wipe-and-replace save
      // would persist — silently reverting the flushed edit.
      if (pendingSaveRef?.current) {
        await pendingSaveRef.current; // allSettled-chained: never rejects
      }
      if (!cancelled) await loadFee();
    })();
    return () => {
      cancelled = true;
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      // Unmount (modal close, tab switch, or entity change) must not drop a
      // pending edit: fire the flush now and park the promise where the
      // modal's close handler can await it. A fetch fired from cleanup still
      // runs to completion — the SPA isn't unloading.
      parkPendingWrite(flushLatestRef.current());
    };
  }, [loadFee, pendingSaveRef, parkPendingWrite]);

  // The fee splits into its primary scope and the probable add-ons, with the
  // quoted total the client sees as the sum. Deliberately NOT called "won"
  // here the way the Budget page's column is: this is the fee editor, and the
  // job itself may not be won yet — the primary scope is simply the part that
  // isn't conditional. Shown as a split because a single "Project total"
  // quietly counts work we may never be engaged for.
  const { sectionsTotal, probableTotal, primaryTotal } = useMemo(() => {
    let quoted = 0;
    let probable = 0;
    for (const s of sections) {
      const sectionSum = s.components.reduce((cs, c) => cs + (c.amount || 0), 0);
      quoted += sectionSum;
      if (s.is_probable) probable += sectionSum;
    }
    return { sectionsTotal: quoted, probableTotal: probable, primaryTotal: quoted - probable };
  }, [sections]);

  // Amend mode kicks in when at least one section has already been used on a
  // progress claim. In that case `update_project_fee` would refuse the wipe-
  // and-replace save (per its new guard), so we route through the additive
  // RPCs instead: append_project_fee_sections for new variation sections,
  // update_project_fee_section for unclaimed existing ones.
  const isAmendMode = useMemo(
    () => sections.some((s) => (s.progress_claim_count ?? 0) > 0),
    [sections]
  );

  const handleCreateFee = useCallback(async () => {
    setCreating(true);
    setError(null);
    try {
      const { error: rpcError } = await supabase.rpc('create_project_fee', {
        p_fee: { project_id: projectId, stage_id: stageId },
        p_sections: null,
      });
      if (rpcError) throw rpcError;
      await loadFee();
    } catch (err) {
      console.error('Failed to create fee:', err);
      notifyReadOnly(err);
      setError(err instanceof Error ? err.message : 'Failed to create fee');
    } finally {
      setCreating(false);
    }
  }, [projectId, stageId, supabase, loadFee]);

  // Resolves true when the fee is persisted (or nothing was dirty), false
  // when the save failed — flush() uses this to surface a failure the inline
  // badge can no longer show because the modal is closing.
  const persistFee = useCallback(async (): Promise<boolean> => {
    if (!fee) return true;
    if (savingRef.current) return true; // a save is already in flight
    savingRef.current = true;

    // Capture what we're about to save up-front so concurrent edits during
    // the await don't corrupt our payloads, id-mapping, or the snapshot.
    const sentSections = sections;

    try {
      if (isAmendMode) {
        // -----------------------------------------------------------------
        // Amend mode: diff against the last-saved snapshot, route each
        // change through the additive / per-section RPC. Locked sections
        // are never sent.
        // -----------------------------------------------------------------
        const prevByKey = new Map<number, string>();
        try {
          (JSON.parse(lastSavedJsonRef.current || '[]') as Array<{ id: number | null } & object>).forEach((s) => {
            if (s.id != null) prevByKey.set(s.id, JSON.stringify(s));
          });
        } catch {
          // bad snapshot — treat everything as dirty
        }

        const dirtyExisting: ProjectFeeSection[] = [];
        const newSections: ProjectFeeSection[] = [];

        for (const cur of sentSections) {
          if (!cur.id) {
            newSections.push(cur);
            continue;
          }
          if ((cur.progress_claim_count ?? 0) > 0) continue; // locked
          const prev = prevByKey.get(cur.id);
          const curSer = JSON.stringify(serializeSectionForCompare(cur));
          if (!prev || prev !== curSer) {
            dirtyExisting.push(cur);
          }
        }

        if (dirtyExisting.length === 0 && newSections.length === 0) {
          // Nothing to send — but the snapshot still has to catch up, or the
          // fee stays dirty forever. A locked section's Probable toggle is
          // persisted by its own RPC yet still changes `sections`, and every
          // locked section is skipped above, so this branch is exactly where
          // that edit lands. Without this the autosave re-fires a no-op on
          // each subsequent change until an unlocked edit happens to refresh
          // the snapshot.
          lastSavedJsonRef.current = JSON.stringify(
            sentSections.map(serializeSectionForCompare)
          );
          setSaveStatus('idle');
          return true;
        }

        setSaveStatus('saving');

        for (const s of dirtyExisting) {
          const { error: e } = await supabase.rpc('update_project_fee_section', {
            p_section_id: s.id,
            p_section: buildSectionPayload(s),
          });
          if (e) throw e;
        }

        // Track new-id-by-local-key so we map RPC results back to the right
        // row even if the user added/removed sections during the await.
        const newIdByLocalKey = new Map<string, number>();
        if (newSections.length > 0) {
          const { data: rpcData, error: e } = await supabase.rpc('append_project_fee_sections', {
            p_fee_id: fee.id,
            p_sections: newSections.map(buildSectionPayload),
          });
          if (e) throw e;
          const result = (rpcData ?? {}) as { inserted_section_ids?: number[] };
          const newIds = result.inserted_section_ids ?? [];
          newSections.forEach((s, i) => {
            if (s._localKey && newIds[i] != null) {
              newIdByLocalKey.set(s._localKey, newIds[i]);
              // Remembered so a removeSection that raced this append can
              // still find (and delete) the row it just created.
              appendedIdByLocalKeyRef.current.set(s._localKey, newIds[i]);
            }
          });

          // Assign the new ids in live state by matching _localKey, so
          // sections inserted (or removed) during the in-flight save can't
          // bind to the wrong row.
          setSections((prev) => prev.map((s) => {
            if (s.id != null) return s;
            const key = s._localKey;
            if (!key) return s;
            const newId = newIdByLocalKey.get(key);
            if (newId == null) return s;
            const { _localKey, ...rest } = s;
            return { ...rest, id: newId, progress_claim_count: 0 };
          }));
        }

        // Snapshot reflects WHAT IS IN DB after this save — i.e. the rows
        // we just sent, with any new ids applied — NOT live state (which
        // may include keystrokes typed during the await). The next diff
        // will catch those as dirty and trigger another save.
        const snapshotSections = sentSections.map((s) => {
          if (s.id != null) return s;
          if (s._localKey && newIdByLocalKey.has(s._localKey)) {
            return { ...s, id: newIdByLocalKey.get(s._localKey)! };
          }
          return s;
        });
        lastSavedJsonRef.current = JSON.stringify(
          snapshotSections.map(serializeSectionForCompare)
        );

        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 1500);
        return true;
      }

      // -------------------------------------------------------------------
      // Non-amend mode: classic full-fee save. Backend now rejects this if
      // any section is referenced by a progress claim — that's our safety
      // net while the local isAmendMode flag is being established.
      // -------------------------------------------------------------------
      setSaveStatus('saving');
      const payload = sentSections.map((s, idx) => ({
        phase: s.phase,
        discipline: s.discipline ?? null,
        sort_order: idx,
        name: s.name,
        notes: s.notes ?? null,
        is_item_based: s.is_item_based ?? false,
        is_probable: s.is_probable ?? false,
        components: s.components.map((c, ci) => ({
          sort_order: ci,
          component_type: c.component_type,
          description: c.description,
          team_member_type: c.team_member_type,
          hours: c.hours,
          rate: c.rate,
          amount: c.amount,
          notes: c.notes ?? null,
        })),
      }));

      const { error: rpcError } = await supabase.rpc('update_project_fee', {
        p_fee_id: fee.id,
        p_fee: { fee_date: fee.fee_date, status: fee.status, notes: fee.notes },
        p_sections: payload,
      });
      if (rpcError) throw rpcError;
      // Snapshot from what we actually sent (sentSections), not the live
      // `sections` which may include keystrokes typed during the await.
      lastSavedJsonRef.current = JSON.stringify(
        sentSections.map(serializeSectionForCompare)
      );
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 1500);
      return true;
    } catch (err) {
      console.error('Failed to save fee:', err);
      lastSaveErrorRef.current = err;
      notifyReadOnly(err);
      setSaveStatus('error');
      // If the backend tells us claims now exist, reload so the UI flips
      // into amend mode and the next save uses the correct RPCs.
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('progress claims')) {
        await loadFee();
      }
      return false;
    } finally {
      savingRef.current = false;
    }
  }, [fee, sections, isAmendMode, supabase, serializeSectionForCompare, buildSectionPayload, loadFee]);

  // Cancel the debounce, wait out any in-flight save, then persist whatever
  // is still dirty. Called by the modal before it closes so the Budget
  // page's refetch-on-close can't race (or miss) a pending fee save. Waits
  // are bounded and failures are surfaced via toast — the close must never
  // hang, and a save silently lost on close must never look successful.
  const flush = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    const idle = await waitForSaveIdle();
    if (!fee) return;
    if (!idle) {
      notifySaveFailure(
        null,
        'A fee save is taking unusually long — recent changes may not have been saved.',
      );
      return;
    }
    const cur = JSON.stringify(sections.map(serializeSectionForCompare));
    if (cur === lastSavedJsonRef.current) return;
    const ok = await persistFee();
    if (!ok) {
      notifySaveFailure(
        lastSaveErrorRef.current,
        'Your fee changes could not be saved. Please reopen the fee editor and try again.',
      );
    }
  }, [fee, sections, serializeSectionForCompare, persistFee, waitForSaveIdle]);

  useEffect(() => {
    flushLatestRef.current = flush;
  });

  useEffect(() => {
    if (!flushRef) return;
    flushRef.current = () => flushLatestRef.current();
    return () => {
      flushRef.current = null;
    };
  }, [flushRef]);

  useEffect(() => {
    if (!fee) return;
    // Autosave only when the sections actually differ from the last-saved
    // snapshot (loadFee seeds it), so the state loadFee itself just set
    // never triggers a save — and a real edit is never missed.
    const cur = JSON.stringify(sections.map(serializeSectionForCompare));
    if (cur === lastSavedJsonRef.current) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    const fire = () => {
      // If a save is already in flight, wait a beat and try again so the
      // user's latest changes aren't dropped.
      if (savingRef.current) {
        autoSaveTimerRef.current = setTimeout(fire, 500);
        return;
      }
      void persistFee();
    };
    autoSaveTimerRef.current = setTimeout(fire, 1500);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [sections, fee, persistFee, serializeSectionForCompare]);

  const updateSection = useCallback((index: number, updates: Partial<ProjectFeeSection>) => {
    setSections(prev => prev.map((s, i) => (i === index ? { ...s, ...updates } : s)));
  }, []);

  // Probable has its own write path because the autosave can't carry it on a
  // billed section: the amend-mode diff skips any section with progress
  // claims (`continue; // locked`), so a toggle there would update the screen
  // and never reach the database. set_fee_section_probable exists precisely
  // for this — it skips the claims guard because the flag moves no money.
  //
  // Unlocked sections need none of this; is_probable rides along in all three
  // section payloads and the normal debounced save persists it.
  const handleProbableChange = useCallback(async (index: number, next: boolean) => {
    const target = sections[index];
    setSections(prev => prev.map((s, i) => (i === index ? { ...s, is_probable: next } : s)));

    const isLocked = (target?.progress_claim_count ?? 0) > 0;
    // An unsaved section has no row to update yet; the pending create/append
    // carries the flag with it.
    if (!isLocked || target?.id == null) return;

    const { error: rpcError } = await supabase.rpc('set_fee_section_probable', {
      p_section_id: target.id,
      p_probable: next,
    });
    if (rpcError) {
      console.error('Failed to set section probable:', rpcError);
      notifyReadOnly(rpcError);
      // Put the checkbox back where the database still has it, so the screen
      // can't claim a change that was refused.
      setSections(prev => prev.map((s, i) => (i === index ? { ...s, is_probable: !next } : s)));
    }
  }, [sections, supabase]);

  const updateComponent = useCallback((sectionIndex: number, componentIndex: number, updates: Partial<ProjectFeeComponent>) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const next = [...s.components];
      const merged = { ...next[componentIndex], ...updates };
      // 'time' (hours × rate) and 'item' (qty × rate) both auto-compute amount.
      if ((merged.component_type === 'time' || merged.component_type === 'item') && ('hours' in updates || 'rate' in updates)) {
        const h = merged.hours ?? 0;
        const r = merged.rate ?? 0;
        merged.amount = Math.round(h * r * 100) / 100;
      }
      next[componentIndex] = merged;
      return { ...s, components: next };
    }));
  }, []);

  const addComponent = useCallback((sectionIndex: number, type: FeeComponentType, defaults?: Partial<ProjectFeeComponent>) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const newComp: ProjectFeeComponent = {
        sort_order: s.components.length,
        component_type: type,
        description: defaults?.description ?? '',
        team_member_type: defaults?.team_member_type ?? null,
        hours: type === 'time' || type === 'item' ? 0 : null,
        rate: type === 'time' || type === 'item' ? 0 : null,
        amount: 0,
        notes: null,
        ...defaults,
      };
      return { ...s, components: [...s.components, newComp] };
    }));
  }, []);

  const removeComponent = useCallback((sectionIndex: number, componentIndex: number) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      return { ...s, components: s.components.filter((_, ci) => ci !== componentIndex) };
    }));
  }, []);

  const removeSection = useCallback((index: number) => {
    const target = sections[index];
    if (!target) return;

    setSections(prev => prev.filter((_, i) => i !== index));

    // Non-amend mode: the wipe-and-replace update_project_fee autosave
    // persists the removal. In amend mode the delete runs right away — a
    // discrete destructive action shouldn't ride the autosave debounce. A
    // section with no id yet is NOT automatically safe to skip: an in-flight
    // autosave may be appending it this very moment, so resolve its id after
    // the save settles (via appendedIdByLocalKeyRef) and delete then.
    if (!isAmendMode) return;

    const removal = (async () => {
      // Wait out an in-flight save so an update/append for this same
      // section can't race the delete.
      const idle = await waitForSaveIdle();
      const sectionId =
        target.id ??
        (target._localKey ? appendedIdByLocalKeyRef.current.get(target._localKey) : undefined);
      if (sectionId == null) return; // never persisted — nothing to delete
      if (!idle) {
        throw new Error('Timed out waiting for a pending fee save');
      }
      const { error: rpcError } = await supabase.rpc('delete_project_fee_section', {
        p_section_id: sectionId,
      });
      if (rpcError) throw rpcError;
      // Drop the section from the snapshot too, so the autosave diff
      // doesn't chase a row that no longer exists on either side.
      try {
        const snap = JSON.parse(lastSavedJsonRef.current || '[]') as Array<{ id: number | null }>;
        lastSavedJsonRef.current = JSON.stringify(snap.filter((s) => s.id !== sectionId));
      } catch {
        // bad snapshot — the next successful save rebuilds it
      }
    })();

    // Park the delete in the shared pending-writes chain so modal close (and
    // a remounted Fees tab) wait for it before refetching.
    parkPendingWrite(removal);

    removal.catch((err) => {
      console.error('Failed to delete fee section:', err);
      notifyReadOnly(err);
      // Persistent toast: the restore below is invisible if the tab already
      // unmounted, and the user must know the section still exists.
      notifySaveFailure(
        err,
        'The fee section could not be deleted — it still exists on the fee. Please reopen the fee editor.',
      );
      setSaveStatus('error');
      // Restore the section so the UI matches the DB again.
      setSections(prev => {
        const next = [...prev];
        next.splice(Math.min(index, next.length), 0, target);
        return next.map((s, i) => ({ ...s, sort_order: i }));
      });
    });
  }, [sections, isAmendMode, supabase, waitForSaveIdle, parkPendingWrite]);

  // Generate a stable client-side key for a newly-created section so the
  // append RPC's returned ids can be mapped back to the right row even if
  // the user reorders or removes sections during the in-flight save.
  const makeLocalKey = () => `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // Sort key for a discipline: structural (and legacy null) first, civil last.
  const disciplineRank = (d: Discipline | null | undefined) => (normDiscipline(d) === 'civil' ? 1 : 0);

  const addPhase = useCallback((phase: FeePhase, discipline: Discipline | null) => {
    setSections(prev => {
      // Standard phases are unique per fee per discipline — block re-adding.
      // Custom phases are ad-hoc and may repeat, so the guard is skipped.
      if (phase !== 'Custom' && prev.some(s => s.phase === phase && normDiscipline(s.discipline) === normDiscipline(discipline))) {
        return prev;
      }
      // The whole point of a Probable section is speed: one figure, entered
      // once, so a job you may not win still shows as pipeline on the Budget
      // page. It arrives with its single lump-sum line already there, so the
      // user types an amount and nothing else. It is always probable — the
      // DB CHECK enforces it and SectionEditor locks the toggle.
      const isProbablePhase = phase === 'Probable';
      const newSection: ProjectFeeSection = {
        fee_id: fee?.id,
        phase,
        discipline,
        sort_order: prev.length,
        name: null,
        fee_total: 0,
        // Site Visit phases default to item (count-based) claiming; everything
        // else defaults to % complete. The user can flip an empty section.
        is_item_based: phase === 'Site Visit',
        is_probable: isProbablePhase,
        components: isProbablePhase
          ? [{
              sort_order: 0,
              component_type: 'allowance' as FeeComponentType,
              description: 'Probable fee',
              team_member_type: null,
              hours: null,
              rate: null,
              amount: 0,
              notes: null,
            }]
          : [],
        _localKey: makeLocalKey(),
      };
      // Group by discipline (structural before civil), then standard phases in
      // canonical lifecycle order, then Custom rows at the end of each group.
      const next = [...prev, newSection].sort((a, b) => {
        const da = disciplineRank(a.discipline);
        const db = disciplineRank(b.discipline);
        if (da !== db) return da - db;
        const ai = a.phase === 'Custom' ? FEE_PHASES.length : FEE_PHASES.indexOf(a.phase);
        const bi = b.phase === 'Custom' ? FEE_PHASES.length : FEE_PHASES.indexOf(b.phase);
        if (ai !== bi) return ai - bi;
        return a.sort_order - b.sort_order;
      });
      return next.map((s, i) => ({ ...s, sort_order: i }));
    });
  }, [fee]);

  // In amend mode the user is adding a variation / extra scope, not picking a
  // missing standard phase. Default to a Custom section pre-filled with the
  // word "Variation" so it's obvious what it represents — they can rename it.
  const addVariationSection = useCallback((discipline: Discipline | null) => {
    setSections(prev => {
      const newSection: ProjectFeeSection = {
        fee_id: fee?.id,
        phase: 'Custom',
        discipline,
        sort_order: prev.length,
        name: 'Variation',
        fee_total: 0,
        components: [],
        _localKey: makeLocalKey(),
      };
      return [...prev, newSection].map((s, i) => ({ ...s, sort_order: i }));
    });
  }, [fee]);

  // Two-way bind the Civil toggle to the project's has_civil_scope flag.
  // Turning it off only hides the civil blocks — civil sections are kept in
  // the DB (and excluded from billing by get_progress_claim_context) so they
  // reappear if civil scope is turned back on.
  const handleToggleCivil = useCallback(async (checked: boolean) => {
    setCivilEnabled(checked);
    civilWriteInFlightRef.current = true;
    try {
      const { data, error: rpcError } = await supabase.rpc('update_project_detail', {
        project_id: projectId,
        project_data: { has_civil_scope: checked },
      });
      if (rpcError) throw rpcError;
      // update_project_detail reports failures in the result, not by throwing.
      if (data && data.success === false) throw new Error(data.error || 'Failed to update civil scope');
    } catch (err) {
      console.error('Failed to update civil scope:', err);
      notifyReadOnly(err);
      setCivilEnabled(!checked); // revert optimistic toggle
    } finally {
      civilWriteInFlightRef.current = false;
    }
  }, [projectId, supabase]);

  // Standard phases not yet present for a given discipline. Custom is handled
  // separately via a dedicated button (multiple Custom rows allowed).
  const phasesAvailableFor = useCallback(
    (discipline: Discipline | null) =>
      FEE_PHASES.filter(p => !sections.some(s => s.phase === p && normDiscipline(s.discipline) === normDiscipline(discipline))),
    [sections]
  );

  const renderSection = (section: ProjectFeeSection, idx: number) => {
    const isLocked = (section.progress_claim_count ?? 0) > 0;
    // In amend mode any unclaimed section can be removed (persisted ones go
    // through delete_project_fee_section); claimed sections are locked.
    const canRemove = isAmendMode ? (section.progress_claim_count ?? 0) === 0 : true;
    return (
      <SectionEditor
        key={section.id ?? section._localKey ?? `new-${idx}`}
        section={section}
        onSectionChange={(updates) => updateSection(idx, updates)}
        onProbableChange={(next) => void handleProbableChange(idx, next)}
        onAddComponent={(type, defaults) => addComponent(idx, type, defaults)}
        onUpdateComponent={(ci, updates) => updateComponent(idx, ci, updates)}
        onRemoveComponent={(ci) => removeComponent(idx, ci)}
        onRemoveSection={() => removeSection(idx)}
        locked={isLocked}
        canRemoveSection={canRemove}
      />
    );
  };

  const renderAddBar = (discipline: Discipline | null) => (
    isAmendMode ? (
      <div className="fees-tab__add-phase">
        <button
          className="fees-tab__add-phase-btn fees-tab__add-phase-btn--variation"
          onClick={() => addVariationSection(discipline)}
          title="Add a new section for variation work or extra scope"
        >
          + Add new section
        </button>
      </div>
    ) : (
      <div className="fees-tab__add-phase">
        <span className="fees-tab__add-phase-label">Add phase:</span>
        {phasesAvailableFor(discipline).map(p => (
          <button key={p} className="fees-tab__add-phase-btn" onClick={() => addPhase(p, discipline)}>
            + {p}
          </button>
        ))}
        <button
          className="fees-tab__add-phase-btn fees-tab__add-phase-btn--custom"
          onClick={() => addPhase('Custom', discipline)}
          title="Add an ad-hoc phase you can name yourself"
        >
          + Custom
        </button>
      </div>
    )
  );

  // Render one discipline's sections + add bar. Sections keep their index into
  // the flat `sections` array so the existing callbacks work unchanged. Civil
  // sections are simply not rendered when civil scope is off (kept in state).
  const renderGroup = (discipline: Discipline | null, showHeading: boolean) => {
    const indexed = sections
      .map((section, idx) => ({ section, idx }))
      .filter(({ section }) => normDiscipline(section.discipline) === normDiscipline(discipline));
    return (
      <div className="fees-tab__discipline-group" key={discipline ?? 'structural'}>
        {showHeading && (
          <h4 className="fees-tab__discipline-heading">{disciplineLabel(discipline)}</h4>
        )}
        {indexed.length === 0 ? (
          <p className="fees-tab__hint">No phases yet. Add one below to start.</p>
        ) : (
          <div className="fees-tab__sections">
            {indexed.map(({ section, idx }) => renderSection(section, idx))}
          </div>
        )}
        {renderAddBar(discipline)}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fees-tab__skeleton" aria-busy="true">
        <Skeleton variant="rectangular" height={56} borderRadius={8} />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={132} borderRadius={8} />
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="fees-tab__state fees-tab__state--error">{error}</div>;
  }

  if (!fee) {
    return (
      <div className="fees-tab fees-tab--empty">
        <div className="fees-tab__empty-card">
          <h4>No fee plan yet</h4>
          <p>Create a draft fee plan, then add the phases that apply to this project.</p>
          <button
            className="fees-tab__create-btn"
            onClick={handleCreateFee}
            disabled={creating}
          >
            {creating ? 'Creating…' : 'Create fee plan'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fees-tab">
      <div className="fees-tab__header">
        <div className="fees-tab__header-meta">
          {/* With nothing probable this reads exactly as it always did — one
              total — so the split only appears when there is something to
              split. */}
          {probableTotal > 0 ? (
            <span className="fees-tab__total fees-tab__total--split">
              <span className="fees-tab__total-part">
                Primary: <strong>{formatCurrency(primaryTotal)}</strong>
              </span>
              <span
                className="fees-tab__total-part fees-tab__total-part--probable"
                title="Quoted but not yet awarded. Excluded from the Budget page's Won Fee and Unallocated columns."
              >
                Probable: <strong>{formatCurrency(probableTotal)}</strong>
              </span>
              <span className="fees-tab__total-part fees-tab__total-part--quoted">
                Quoted total: <strong>{formatCurrency(sectionsTotal)}</strong>
              </span>
            </span>
          ) : (
            <span className="fees-tab__total">
              Project total: <strong>{formatCurrency(sectionsTotal)}</strong>
            </span>
          )}
        </div>
        <div className="fees-tab__header-actions">
          <label className="fees-tab__civil-toggle" title="Split this fee plan into separate Structural and Civil disciplines">
            <input
              type="checkbox"
              checked={civilEnabled}
              onChange={(e) => handleToggleCivil(e.target.checked)}
            />
            Include Civil
          </label>
          <span className="fees-tab__save-status">
            {saveStatus !== 'idle' && (
              <SaveDot
                state={saveStatus}
                label={
                  saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : 'Save failed'
                }
              />
            )}
          </span>
        </div>
      </div>

      {isAmendMode && (
        <div className="fees-tab__amend-banner">
          This fee has been used on progress claims. Billed sections are
          locked to preserve invoice history — add a new variation section
          below for additional scope or extra inspections.
        </div>
      )}

      {civilEnabled
        ? DISCIPLINES.map(d => renderGroup(d, true))
        : renderGroup(null, false)}
    </div>
  );
};

export default FeesTab;
